class QuickAddDrawer extends HTMLElement {
  constructor() {
    super();
    this.currentProduct = null;
    this.setupDrawer();
    this.setupTriggers();
  }

  setupDrawer() {
    const closeButton = this.querySelector('.drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    const overlay = this.querySelector('[data-quick-add-mobile-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });
  }

  setupTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add-trigger]');
      if (trigger) {
        event.preventDefault();

        const productData = trigger.dataset.productJson;
        if (productData) {
          try {
            const product = JSON.parse(productData);
            this.openWithProduct(product, trigger);
          } catch (error) {
            console.error('Failed to parse product data:', error);
          }
        }
      }
    });
  }

  openWithProduct(product, triggeredBy) {
    this.currentProduct = product;
    this.activeElement = triggeredBy;
    this.updateContent(product);
    this.open();
  }

  updateContent(product) {
    const vendorElement = this.querySelector('[data-product-vendor]');
    if (vendorElement) {
      vendorElement.textContent = product.vendor;
    }

    const titleElement = this.querySelector('[data-product-title]');
    if (titleElement) {
      titleElement.textContent = product.title;
    }

    const priceContainer = this.querySelector('[data-product-price-container]');
    const regularPriceElement = this.querySelector('[data-product-price]');
    const comparePriceElement = this.querySelector('[data-product-compare-price]');
    const salePriceElement = this.querySelector('[data-product-sale-price]');

    if (priceContainer && regularPriceElement) {
      if (product.compareAtPrice) {
        // Product is on sale
        priceContainer.classList.add('price--on-sale');
        comparePriceElement.textContent = product.compareAtPrice;
        salePriceElement.textContent = product.price;
      } else {
        // Regular price only
        priceContainer.classList.remove('price--on-sale');
        regularPriceElement.textContent = product.price;
      }
    }

    const imagesContainer = this.querySelector('[data-product-images]');
    if (imagesContainer && product.images) {
      const ratioPercent = product.aspectRatio ? (1 / product.aspectRatio * 100) : 100;

      imagesContainer.innerHTML = product.images.map(image => `
        <div class="quick-add-mobile-drawer__image-cell" style="--ratio-percent: ${ratioPercent}%;">
          <img
            src="${image.url}"
            srcset="${image.srcset}"
            sizes="250px"
            alt="${image.alt || ''}"
            loading="lazy"
            width="${image.width}"
            height="${image.height}"
            class="quick-add-mobile-drawer__image"
          >
        </div>
      `).join('');
    }

    const sizesContainer = this.querySelector('[data-size-buttons]');
    if (sizesContainer && product.sizes) {
      // Add one-size class if there's only one size
      if (product.sizes.length === 1) {
        sizesContainer.classList.add('quick-add-mobile-drawer__size-buttons--one-size');
      } else {
        sizesContainer.classList.remove('quick-add-mobile-drawer__size-buttons--one-size');
      }

      sizesContainer.innerHTML = product.sizes.map(size => {
        const isSoldOut = !size.available;
        const hasNotifyMe = product.notifyMeEnabled;
        const showNotifyMe = isSoldOut && hasNotifyMe;

        return `
          <button
            class="quick-add-mobile-drawer__size-btn ${isSoldOut ? 'quick-add-mobile-drawer__size-btn--sold-out' : ''}"
            data-variant-id="${size.variantId}"
            data-size="${size.value}"
            data-product-id="${product.id}"
            type="button"
            ${isSoldOut && !hasNotifyMe ? 'disabled aria-disabled="true"' : ''}
            ${showNotifyMe ? 'data-notify-me-trigger data-variant-id-notify="' + size.variantId + '"' : ''}
            aria-label="${showNotifyMe ? 'Notify me when size ' + size.value + ' is back in stock' : 'Add size ' + size.value + ' to cart'}"
          >
            <span class="quick-add-mobile-drawer__size-text${showNotifyMe ? ' quick-add-mobile-drawer__size-text--strikethrough' : ''}">${size.value}</span>
            ${showNotifyMe ? `
              <span class="quick-add-mobile-drawer__notify-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                  <rect x="3.92857" y="4.42857" width="9.14286" height="7.14286" stroke="#B8B9BA" stroke-width="0.857143"/>
                  <path d="M3.75 5.42859C4.15 5.42859 7.16667 7.42859 8.5 8.28573L13.25 5.42859" stroke="#B8B9BA" stroke-width="0.857143"/>
                </svg>
              </span>
            ` : ''}
          </button>
        `;
      }).join('');

      this.setupSizeButtons();
    }
  }

  openNotifyMe(variantId) {
    const notifyMeDrawer = document.querySelector('notify-me[data-notify-me-drawer]');
    if (!notifyMeDrawer) return;

    // Store product data before closing (since close might clear it)
    const productData = this.currentProduct;

    this.close();

    if (productData && typeof notifyMeDrawer.openWithProduct === 'function') {
      notifyMeDrawer.openWithProduct(productData, variantId);
    }
  }

  setupSizeButtons() {
    const sizeButtons = this.querySelectorAll('.quick-add-mobile-drawer__size-btn');

    sizeButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();

        // Check if this is a notify-me button
        if (button.hasAttribute('data-notify-me-trigger')) {
          const variantId = button.dataset.variantIdNotify;
          this.openNotifyMe(variantId);
          return;
        }

        // Regular add to cart functionality
        if (button.disabled) return;

        const variantId = button.dataset.variantId;
        const originalContent = button.innerHTML;

        button.classList.add('quick-add-mobile-drawer__size-btn--loading');
        button.disabled = true;

        try {
          await this.addToCart(variantId);

          button.classList.remove('quick-add-mobile-drawer__size-btn--loading');
          button.classList.add('quick-add-mobile-drawer__size-btn--success');

          setTimeout(() => {
            this.close();
          }, 500);

        } catch (error) {
          button.classList.remove('quick-add-mobile-drawer__size-btn--loading');
          button.disabled = false;
          button.innerHTML = originalContent;
          console.error('Failed to add to cart:', error);
        }
      });
    });
  }

  async addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    const cart = cartDrawer || cartNotification;

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript, application/json'
      },
      body: formData
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Fire GTM event for Quick Add tracking
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          event: 'quick_add_click',
          event_category: 'ecommerce',
          event_action: 'quick_add',
          product_id: responseData.product_id,
          variant_id: variantId,
          product_title: responseData.product_title,
          price: responseData.price,
          source: 'quick-add-drawer',
          page_type: window.theme.currentTemplate
        });
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-add-drawer',
          productVariantId: variantId,
          cartData: responseData,
        });
      }

      this.openCart(responseData);
      return responseData;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  openCart(response) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');

    if (cartDrawer) {
      // Remove is-empty class before rendering - this prevents trapFocus errors
      if (cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }

      cartDrawer.renderContents(response);
    } else if (cartNotification) {
      cartNotification.renderContents(response);
    }

    if (window.Shopify && window.Shopify.cartUpdate) {
      window.Shopify.cartUpdate(response);
    }
  }

  open() {
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');

    this.addEventListener('transitionend', () => {
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (window.trapFocus && focusElement) {
        window.trapFocus(this, focusElement);
      }
    }, { once: true });
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    if (window.removeTrapFocus && this.activeElement) {
      window.removeTrapFocus(this.activeElement);
    }

    this.currentProduct = null;
  }
}

customElements.define('quick-add-drawer', QuickAddDrawer);